/**
 * Error Reporter - Core form validation and element wrapper framework.
 * Provides FormValidator and FormElementWrapper classes for form validation,
 * along with helper functions for form manipulation and event handling.
 */

// Declare external dependencies

// Types and Interfaces
interface ChangeManagement {
  registerForm: (form: FormValidatorInstance) => void;
  suspend: (element?: FormElementWrapperInstance) => void;
  resume: (element?: FormElementWrapperInstance) => void;
  fieldChanged: (element: FormElementWrapperInstance) => void;
  fieldBlur: (element: FormElementWrapperInstance) => void;
  keyPress: () => void;
  record: (form: FormValidatorInstance) => void;
  allowLeave: () => void;
  holdLeave: () => void;
}

interface FormElementInterface {
  name: string;
  type: string;
  form: HTMLFormElement;
  value: string;
  checked?: boolean;
  defaultChecked?: boolean;
  defaultValue?: string;
  disabled?: boolean;
  maxLength?: number;
  options?: HTMLOptionsCollection;
  focus: () => void;
  onfocus?: (() => void) | null;
  onblur?: (() => void) | null;
  onclick?: (() => void) | null;
  onchange?: (() => void) | null;
}

interface EventObjectInstance {
  code: string | (() => void);
  type: string;
  which: number | null;
  getCode: () => (() => void);
}

interface FormValidatorInstance {
  name: string;
  element: HTMLFormElement;
  imgPrefix: string;
  changeManagement: boolean | undefined;
  preSubmitFn: ((event: string | null, url: string | null) => boolean | void)[];
  formErrorImage: (HTMLImageElement | null)[];
  invalidCount: number;
  requiredCount: number;
  childElements: { [key: string]: FormElementWrapperInstance };
  fieldErrorOn: HTMLImageElement;
  fieldErrorOff: HTMLImageElement;
  fieldErrorRequiredRed: HTMLImageElement;
  fieldErrorRequiredBlack: HTMLImageElement;
  formErrorOn: HTMLImageElement;
  formErrorOff: HTMLImageElement;
  formErrorRequired: HTMLImageElement;
  errorImageOn: string;
  errorImage?: string;
  setAction: (newAction: string) => void;
  getElement: (elementName: string) => FormElementWrapperInstance | null;
  getValue: (name: string) => string | null;
  setValue: (name: string, val: string) => void;
  displayError: (showError: boolean) => void;
  setValidation: (elementName: string, validationFunction: (() => boolean) | null, required?: boolean) => FormElementWrapperInstance | null;
  validate: () => boolean;
  preSubmit: (event: string | null, url: string | null) => boolean;
  registerPreSubmit: (fn: (event: string | null, url: string | null) => boolean | void) => void;
  saveForm: () => void;
  saveAndRefresh: () => void;
  saveAndRenavigate: () => void;
  introspectElements: () => void;
  changeRequired: (diff: number) => void;
}

interface FormElementWrapperInstance {
  formObj: FormValidatorInstance;
  name: string;
  type: string;
  events: EventObjectInstance[];
  element: FormElementInterface | FormElementInterface[];
  image?: HTMLImageElement;
  checkValid: (returnCompare?: boolean) => boolean | ((value: any) => number | null);
  message: string;
  required: boolean;
  isValid: boolean;
  lastVal: string;
  trappedElements?: number;
  alwaysValidate?: boolean;
  proxyFor?: string | FormElementWrapperInstance;
  proxy?: FormElementWrapperInstance;
  resetting?: boolean;
  aggressiveOnChange?: boolean;
  zesty?: ZestyEditor;
  maxLength?: number;
  keyupStarted?: boolean;
  converter?: (value: string) => any;
  numSelected?: () => number;
  plainText?: boolean;
  validator2?: any;
  get: (which?: string) => string;
  set: (val: string, which?: string) => void;
  initSetGet: (type: string) => void;
  init: () => void;
  addElement: (element: FormElementInterface) => void;
  setValidation: (validationFunction: (() => boolean) | null, required?: boolean, alwaysValidate?: boolean | null) => FormElementWrapperInstance;
  setRequired: (required: boolean) => FormElementWrapperInstance;
  isCollapsed: () => boolean;
  reset: () => string;
  validate: () => boolean;
  addEvent: (event: string, functionName: string) => void;
  focus: () => void;
  trapEvents: () => void;
  registerEvent: (code: string | (() => void), eventName: string, which: number | null) => void;
  executeEvent: (type: string, which?: number) => void;
  forceError: (mssg?: string | null) => void;
  showStatus: () => void;
  getSibling: (name: string) => FormElementWrapperInstance | null;
  validateSibling: (sibling: string | FormElementWrapperInstance) => void;
  setMessage: (mssg: string) => boolean;
  compare: (value: any) => number | null;
  compareNullable: (val: any, op: string, nullRtn?: boolean) => boolean;
  hasChanged: (ignoreSelect?: boolean) => boolean;
}

interface ZestyEditor {
  contentAreaContainer: HTMLElement;
  getContainer: () => HTMLElement;
}

// Module flags
const _ERROR_REPORTER_ = true;
// Note: _SELECT_TOOLS_ is defined by selecttools.ts on globalThis, not here
let _VALIDATE_GROUP_: boolean | undefined;
let _CM_: ChangeManagement | undefined;

/**
 * Get the ChangeManagement instance from globalThis.
 * This is needed because _CM_ may be defined by changemanagement.ts which could load after this module.
 */
function getCM(): ChangeManagement | undefined {
  return (globalThis as any)._CM_ as ChangeManagement | undefined;
}

/**
 * Check if selecttools module is loaded by checking globalThis.
 * This is needed because selecttools.ts may load before or after this module.
 */
function isSelectToolsLoaded(): boolean {
  return (globalThis as any)._SELECT_TOOLS_ === true;
}

// Image file names
const fieldProblem = "fieldproblem.gif";
const fieldOff = "fieldoff.gif";
const fieldRequiredRed = "fieldrequiredblack.gif";
const fieldRequiredBlack = "fieldrequiredblack.gif";
const formProblem = "formproblem.gif";
const formOff = "formoff.gif";
const formRequired = "formrequired.gif";

// Module state
let formElements: any[] = [];
let formObjs: { [key: string]: FormValidatorInstance } = {};
let curFormObj: FormValidatorInstance = null as any;
let curField: FormElementWrapperInstance | null = null;
let firstField: FormElementWrapperInstance | undefined;
let focusField: string | boolean | null = null;
let errorImgPath: string = "";
let validateIsOff = false;
let mssgWin: any;
let debug = false;
let submitInitiated = false;
const VALUE_SPACER = ",";
let processSubmitParams: ((form: FormValidatorInstance, event: string | null, url: string | null) => boolean | void) | undefined;

/**
 * Set the current form object and sync to globalThis for external access.
 * @param obj - The FormValidator instance to set as current
 */
function setCurFormObj(obj: FormValidatorInstance): void {
  curFormObj = obj;
  (globalThis as any).curFormObj = obj;
}

/**
 * Set the current field and sync to globalThis for external access.
 * @param field - The FormElementWrapper instance to set as current, or null
 */
function setCurField(field: FormElementWrapperInstance | null): void {
  curField = field;
  (globalThis as any).curField = field;
}

// Initialize focusField
if (focusField == null)
  focusField = (firstField != null) ? firstField.name : true;

//================================== Public Helper Functions ==================================

/**
 * Get a form validator object by name.
 * @param name - The form name
 * @returns The FormValidator object
 */
function getFormObj(name: string): FormValidatorInstance {
  return formObjs[name];
}

/**
 * Get an element wrapper object by name from any form.
 * @param name - The element name
 * @returns The FormElementWrapper or null
 */
function getElementObj(name: string): FormElementWrapperInstance | null {
  for (const i in formObjs)
    if (formObjs[i].getElement(name) != null)
      return formObjs[i].getElement(name);
  return null;
}

/**
 * Get element object by CSS selector using native querySelector.
 * @param selector - CSS selector
 * @returns The FormElementWrapper or null
 */
function getEl(selector: string): FormElementWrapperInstance | null {
  const el = document.querySelector<HTMLElement>(selector);
  if (!el) return null;
  const name = el.getAttribute("name");
  return name ? getElementObj(name) : null;
}

/**
 * Get element object by jQuery selector.
 * @param query - jQuery selector
 * @returns The FormElementWrapper or null
 * @deprecated Use getEl() instead which uses native querySelector
 */
function get$(query: string): FormElementWrapperInstance | null {
  console.warn("get$() is deprecated. Use getEl() instead which uses native document.querySelector(). Will be removing get$() in future versions.");
  return getElementObj($(query).attr("name"));
}

/**
 * Get value of an element by name.
 * @param name - Element name
 * @returns The element value or null
 */
function getValue(name: string): string | null {
  const e = getElementObj(name);
  return e == null ? null : e.get();
}

/**
 * Set value of an element by name.
 * @param name - Element name
 * @param val - Value to set
 */
function setValue(name: string, val: string): void {
  const el = getElementObj(name);
  if (el) el.set(val);
}

/**
 * Get form validator for an element.
 * @param element - The form element
 * @returns The FormValidator
 */
function getFormValidator(element: { name: string }): FormValidatorInstance {
  return getFormObj(element.name);
}

/**
 * Get the FormElementWrapper for a raw DOM element.
 * @param element - The DOM element
 * @returns The FormElementWrapper
 */
function getFormElementWrapper(element: FormElementInterface): FormElementWrapperInstance {
  const formName = element.form.name;
  const elementName = element.name;
  return getFormObj(formName).getElement(elementName)!;
}

/**
 * Enable form validation globally.
 */
function enableValidation(): void {
  validateIsOff = false;
  for (const i in formObjs) {
    const children = formObjs[i].childElements;
    for (const j in children)
      children[j].showStatus();
  }
}

/**
 * Disable form validation globally.
 */
function disableValidation(): void {
  validateIsOff = true;
  for (const i in formObjs) {
    const formObj = formObjs[i];
    const children = formObj.childElements;
    const offImgName = formObj.fieldErrorOff.src;
    for (const j in children) {
      children[j].isValid = true;
      if (children[j].image) children[j].image!.src = offImgName;
    }
    formObj.invalidCount = 0;
    formObj.displayError(false);
  }
}

/**
 * Set the path for error indicator images.
 * @param path - The image path
 */
function setImagePath(path: string): void {
  errorImgPath = path;
}

/**
 * Submit a form with validation.
 * @param formName - The form name
 * @param event - The event type
 * @param url - The URL to submit to
 */
function submitForm(formName: string | null, event?: string | null, url?: string | null): void {
  submitFormVia(formName, event ?? null, url ?? null, submitNatively);
}

/** Sends a prepared form the ordinary way: a page post to its action. */
const submitNatively = (formObj: FormValidatorInstance): void => formObj.element.submit();

/**
 * The sender of the submit whose pipeline is running, when it is not {@link submitNatively}. A
 * pre-submit check that holds the submit back (`bsCheck.testSubmit`) reads it, so the submit it
 * replays later goes out the same way.
 */
let pendingSender: ((formObj: FormValidatorInstance) => void) | null = null;

/**
 * The sender of the submit whose pipeline is running, for a pre-submit check to replay a held-back
 * submit through; null while no pipeline runs or the submit is an ordinary page post.
 */
function getPendingSubmitSender(): ((formObj: FormValidatorInstance) => void) | null {
  return pendingSender;
}

/**
 * Run a form's submit pipeline (validation, pre-submit hooks, submit events, change tracking) after
 * the same short delay as {@link submitForm}, then hand the prepared form to `send` instead of always
 * submitting it natively.
 * @param formName - The form name; null or empty keeps the current form
 * @param event - The event type, or null for the form's default event
 * @param url - The URL to submit to, or null to keep the form's action
 * @param send - Sends the prepared form; called only when the pipeline lets the submit through
 */
function submitFormVia(formName: string | null, event: string | null, url: string | null, send: (formObj: FormValidatorInstance) => void): void {
  if (formName != null && formName > "") {
    setCurFormObj(getFormObj(formName));
  }
  window.setTimeout(function() {
    pendingSender = send === submitNatively ? null : send;
    let letThrough: boolean;
    try {
      letThrough = doSubmit(event, url);
    } finally {
      pendingSender = null;
    }
    if (letThrough) {
      send(curFormObj);
    }
  }, 100);
}

/** Event that commits the form and then reloads the page it was posted from. */
const COMMIT_AND_REFRESH_EVENT = "commitAndRefresh";
/** Request parameter naming the page an `/action` save was posted from; mirrors `ActionServlet.RENAVIGATE_FROM`. */
const RENAVIGATE_FROM_PARAM = "_renavigateFrom";
/** The form's anti-replay field. */
const UNIQUE_FORM_ID_FIELD = "_uniqueFormId";

/** What `/action` returns for an asynchronous save. */
interface ActionSaveResult {
  renavigateUrl?: string;
  /** The anti-replay id for posting the form again; mirrors `ActionServlet.RENAVIGATE_UNIQUE_FORM_ID`. */
  renavigateUniqueFormId?: string;
  uniqueFormIdError?: string;
  errors?: { description?: string | null; exception?: string | null }[];
}

/** Where the browser goes after a successful asynchronous save. */
interface PageNavigator {
  open: (url: string) => void;
  reload: () => void;
}

/** The browser's own navigation. */
const browserNavigator: PageNavigator = {
  open: url => {
    location.href = url;
  },
  reload: () => location.reload()
};

/**
 * Save a prepared form through `/action` instead of a page post, then open the page the server says
 * re-opens what the save created. A save that created nothing to re-open reloads the current page.
 * A failed save stays on the page with the user's input intact: the errors are shown and the form's
 * anti-replay id is replaced so the next save is not rejected as a repeat.
 * @param formObj - The form, already through the submit pipeline
 * @param navigator - Where to go after a successful save
 */
async function saveViaAction(formObj: FormValidatorInstance, navigator: PageNavigator = browserNavigator): Promise<void> {
  const form = formObj.element;
  const formData = new FormData(form);
  formData.append(RENAVIGATE_FROM_PARAM, location.pathname + location.search);
  const isMultipart = form.enctype === "multipart/form-data";
  const body = isMultipart ? formData : new URLSearchParams(formData as unknown as Record<string, string>);
  let result: ActionSaveResult;
  try {
    const response = await fetch("/action" + location.search, { method: "POST", body });
    result = await response.json() as ActionSaveResult;
  } catch (ex) {
    reportFailedSave(["The server could not be reached or did not answer: " + ex]);
    return;
  }
  const failures = (result.errors ?? [])
    .map(error => error.description || error.exception || "")
    .filter(message => message > "");
  if (result.uniqueFormIdError) {
    failures.unshift(result.uniqueFormIdError);
  }
  if (failures.length > 0) {
    const uniqueFormId = form.elements.namedItem(UNIQUE_FORM_ID_FIELD);
    if (result.renavigateUniqueFormId && uniqueFormId instanceof HTMLInputElement) {
      uniqueFormId.value = result.renavigateUniqueFormId;
    }
    reportFailedSave(failures);
    return;
  }
  if (result.renavigateUrl) {
    navigator.open(result.renavigateUrl);
  } else {
    navigator.reload();
  }
}

/**
 * Tell the user an asynchronous save failed, and turn change tracking and the leave warning back on;
 * the submit pipeline turned both off expecting the page to be left.
 * @param messages - What went wrong, one entry per error
 */
function reportFailedSave(messages: string[]): void {
  const cm = getCM();
  if (cm) {
    cm.resume();
    cm.holdLeave();
  }
  window.alert("Your save was not completed.\n\n" + messages.join("\n"));
}

//---------------------------------- General Purpose ----------------------------------
const LOWERALPHA = "abcdefghijklmnopqrstuvwxyz";
const UPPERALPHA = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const ALPHA = LOWERALPHA + UPPERALPHA;
const NUMERIC = "0123456789";
const ALPHANUMERIC = ALPHA + NUMERIC;

/**
 * Extract characters from string that are in the mask.
 * @param str - Input string
 * @param mask - Characters to keep
 * @returns Filtered string
 */
function extract(str: string, mask: string): string {
  let out = "";
  for (let i = 0; i < str.length; i++)
    if (mask.indexOf(str.charAt(i)) != -1)
      out += str.charAt(i);
  return out;
}

/**
 * Strip characters from string that are in the mask.
 * @param str - Input string
 * @param mask - Characters to remove
 * @returns Filtered string
 */
function strip(str: string, mask: string): string {
  let out = "";
  for (let i = 0; i < str.length; i++)
    if (mask.indexOf(str.charAt(i)) == -1)
      out += str.charAt(i);
  return out;
}

/**
 * Replace all occurrences of a substring.
 * @param str - Input string
 * @param from - Substring to find
 * @param to - Replacement string
 * @returns Modified string
 */
function replace(str: string, from: string, to: string): string {
  for (let i = str.indexOf(from); i != -1; i = str.indexOf(from, i)) {
    str = str.substring(0, i) + to + str.substring(i + from.length);
    i += to.length;
  }
  return str;
}

// Base static resource path (not needed for h2, use if static resources are ever pushed to a CDN)
const BASE_STATIC_RESOURCE = "";

/**
 * Preload an image.
 * @param name - Image path
 * @returns The preloaded Image object
 */
function preloadImg(name: string): HTMLImageElement {
  const img = new Image();
  if (name.substring(0, 8) == "/images/") name = BASE_STATIC_RESOURCE + name;
  img.src = name;
  return img;
}

/**
 * Create a set object from arguments.
 * @param arg - First value or array of values
 * @returns Object with values as keys
 */
function makeSet(arg: any): { [key: string]: boolean } {
  const array = (typeof arg == "object") ? arg : arguments;
  const len = array.length;
  const set: { [key: string]: boolean } = {};
  for (let i = 0; i < len; i++)
    set[array[i]] = true;
  return set;
}

//---------------------------------- Basic Validators ----------------------------------

/**
 * Validate that a required field has a value.
 * @this FormElementWrapperInstance
 * @returns true if valid
 */
function validateRequired(this: FormElementWrapperInstance): boolean {
  const len = this.get().length;
  const max = this.maxLength;
  if (max && len > max) {
    return this.setMessage("The entry cannot be longer than " + max + " character" + (max > 1 ? "s" : "") + ".");
  }
  return this.setMessage(this.required && this.get() == "" ? "This is a required field." : "");
}

/**
 * Validate using a proxy element.
 * @this FormElementWrapperInstance
 * @returns true if valid
 */
function validateProxy(this: FormElementWrapperInstance): boolean {
  if (typeof this.proxy == "object") {
    return this.setMessage(this.proxy!.message);
  }
  return true;
}

//================================== FormValidator class ==================================

/**
 * Form validator constructor function for managing form validation state.
 */
function FormValidator(this: FormValidatorInstance, formName: string, imgPrefix?: string | null, simpleStatus?: boolean, changeManagement?: boolean): void {
  const formElement = (document.forms as any)[formName] as HTMLFormElement;
  if (imgPrefix == null) imgPrefix = "I_";
  const t = this;
  t.name = formName;
  t.element = formElement;
  t.imgPrefix = imgPrefix;
  t.changeManagement = changeManagement;
  t.preSubmitFn = [];

  t.formErrorImage = [];
  let i = 0;
  do {
    t.formErrorImage[i] = (document.images as any)[UPPERALPHA.charAt(i) + imgPrefix + formName] as HTMLImageElement | null;
  } while (t.formErrorImage[i++] != null);

  t.invalidCount = 0;
  t.requiredCount = 0;

  formElement.addEventListener("submit", submitEvent);

  formObjs[formName] = this;
  setCurFormObj(this);

  t.fieldErrorOn = preloadImg(errorImgPath + fieldProblem);
  t.fieldErrorOff = preloadImg(errorImgPath + fieldOff);
  t.fieldErrorRequiredRed = preloadImg(errorImgPath + ((simpleStatus) ? fieldOff : fieldRequiredRed));
  t.fieldErrorRequiredBlack = preloadImg(errorImgPath + ((simpleStatus) ? fieldOff : fieldRequiredBlack));
  if (t.formErrorImage[0] == null) {
    t.formErrorOn = t.formErrorOff = t.formErrorRequired = preloadImg(errorImgPath + formOff);
  } else {
    t.formErrorOn = preloadImg(errorImgPath + formProblem);
    t.formErrorOff = preloadImg(errorImgPath + formOff);
    t.formErrorRequired = preloadImg(errorImgPath + ((simpleStatus) ? formOff : formRequired));
    t.errorImageOn = t.formErrorOff.src;
  }

  t.introspectElements();
  const cm = getCM();
  if (cm) cm.registerForm(this);
  window.dispatchEvent(new Event("formValidatorLoaded"));
}

FormValidator.prototype.setAction = function(this: FormValidatorInstance, newAction: string): void {
  const oldAction = this.getElement("_oldAction");
  if (oldAction != null) oldAction.set(this.element.action);
  this.element.action = newAction;
};

FormValidator.prototype.getElement = function(this: FormValidatorInstance, elementName: string): FormElementWrapperInstance | null {
  return this.childElements[elementName] ?? null;
};

FormValidator.prototype.getValue = function(this: FormValidatorInstance, name: string): string | null {
  const e = this.getElement(name);
  return e == null ? null : e.get();
};

FormValidator.prototype.setValue = function(this: FormValidatorInstance, name: string, val: string): void {
  const el = this.getElement(name);
  if (el) el.set(val);
};

FormValidator.prototype.displayError = function(this: FormValidatorInstance, showError: boolean): void {
  const t = this;
  let newErrorImg = t.formErrorOn.src;
  if (showError == false)
    newErrorImg = (t.requiredCount > 0) ? t.formErrorRequired.src : t.formErrorOff.src;
  if (t.errorImageOn != newErrorImg && t.formErrorImage[0] != null) {
    for (const i in t.formErrorImage)
      if (t.formErrorImage[i] != null) t.formErrorImage[i]!.src = newErrorImg;
    t.errorImageOn = newErrorImg;
  }
};

FormValidator.prototype.setValidation = function(this: FormValidatorInstance, elementName: string, validationFunction: (() => boolean) | null, required?: boolean): FormElementWrapperInstance | null {
  const el = this.childElements[elementName];
  if (el) {
    return el.setValidation(validationFunction, required);
  } else {
    return null;
  }
};

FormValidator.prototype.validate = function(this: FormValidatorInstance): boolean {
  if (validateIsOff) return true;
  let first = true;
  const t = this;
  for (const i in t.childElements)
    if (!t.childElements[i].validate() && first) {
      const focusName = i;
      window.setTimeout(function() { getFormObj(t.name).getElement(focusName)?.focus(); }, (t.formErrorImage[0] != null) ? 1500 : 100);
      first = false;
    }
  t.displayError(t.invalidCount > 0);
  return t.invalidCount == 0;
};

FormValidator.prototype.preSubmit = function(this: FormValidatorInstance, event: string | null, url: string | null): boolean {
  for (const i in this.preSubmitFn) {
    if (this.preSubmitFn[i].call(this, event, url) === false) {
      return false;
    }
  }
  return true;
};

FormValidator.prototype.registerPreSubmit = function(this: FormValidatorInstance, fn: (event: string | null, url: string | null) => boolean | void): void {
  if (typeof fn == "function") {
    this.preSubmitFn.push(fn);
  }
};

/**
 * Save this form exactly as its Save button does: its default event, posted to its own action.
 */
FormValidator.prototype.saveForm = function(this: FormValidatorInstance): void {
  submitForm(this.name);
};

/**
 * Save this form and reload this page, as its Save and Refresh button does. The post goes to this
 * page rather than to the form's action, which names where a plain save lands next. When the save
 * creates a new Relate entry, the reload opens that entry rather than a blank create form.
 */
FormValidator.prototype.saveAndRefresh = function(this: FormValidatorInstance): void {
  submitForm(this.name, COMMIT_AND_REFRESH_EVENT, location.pathname + location.search);
};

/**
 * Save this form in the background, then open what the save created, such as a new Relate entry. A
 * save that created nothing reloads the current page. A failed save keeps the page and the user's
 * input, and reports the errors.
 */
FormValidator.prototype.saveAndRenavigate = function(this: FormValidatorInstance): void {
  submitFormVia(this.name, null, null, formObj => {
    void saveViaAction(formObj);
  });
};

FormValidator.prototype.introspectElements = function(this: FormValidatorInstance): void {
  const t = this;
  const formElement = t.element;
  t.childElements = {};

  // Find all input elements in the form
  const inputElements = formElement.querySelectorAll("input, select, textarea, button");

  inputElements.forEach(function(el) {
    const element = el as unknown as FormElementInterface;
    const name = element.name;
    const type = element.type;
    if ((name != null && name.length > 0) && (type != null && type.length > 0) && type != "button" && type != "submit" && type != "reset" && type != "image") {
      let childElement = t.childElements[name];
      if (childElement == null) {
        childElement = new (FormElementWrapper as any)(element, t);
        t.childElements[name] = childElement;
      } else {
        childElement.addElement(element);
      }
      const htmlElement = el as HTMLElement;
      if ((focusField == true && type != "hidden" && !element.disabled && htmlElement.checkVisibility()) || focusField == name) {
        if (focusField == name || getAndSetType(type) != "select") {
          childElement.focus();
        }
        focusField = null;
      }
    }

    // Add event listeners
    el.addEventListener("focus", function() {
      doMessage(el as HTMLElement);
    });
    el.addEventListener("click", function() {
      doMessage(el as HTMLElement);
    });
    el.addEventListener("blur", function() {
      const fieldMessageEl = document.getElementById("fieldMessage");
      if (fieldMessageEl) fieldMessageEl.style.display = 'none';
    });

    // Add keyup handler for elements with maxlength
    const maxLengthAttr = el.getAttribute("maxlength");
    if (maxLengthAttr) {
      el.addEventListener("keyup", function() {
        doMessage(el as HTMLElement, maxLengthAttr);
      });
    }
  });

  function doMessage(inputEl: HTMLElement, maxLength?: number | string): void {
    let charsLeft: number | null = null;
    const inputValue = (inputEl as HTMLInputElement).value || "";
    if (maxLength) {
      charsLeft = Number(maxLength) - inputValue.length;
    }
    const form = inputEl.closest("form");
    const formName = form?.getAttribute("name") || "";
    const elName = inputEl.getAttribute("name") || "";
    const el = getFormObj(formName)?.getElement(elName);
    const msg = el ? el.message : null;
    if (el && el.maxLength && el.type == "textarea" && !el.keyupStarted) {
      el.keyupStarted = true;
      maxLength = el.maxLength;
      inputEl.addEventListener("keyup", function() {
        doMessage(inputEl, el.maxLength);
      });
    }
    if (msg && msg.length > 0) {
      fieldMessage(msg, inputEl);
    } else if (charsLeft != null) {
      const ml = Number(maxLength);
      if (charsLeft < 0) {
        fieldMessage((-charsLeft) + " characters too many.", inputEl);
      } else if (charsLeft <= (ml > 500 ? 50 : ml > 30 ? ml / 10 : 3)) {
        fieldMessage(charsLeft + " characters left.", inputEl);
      } else {
        fieldMessage();
      }
    } else {
      fieldMessage();
    }
  }
};

FormValidator.prototype.changeRequired = function(this: FormValidatorInstance, diff: number): void {
  const t = this;
  const changeImage = (t.requiredCount == 0 && diff > 0) || (t.requiredCount > 0 && t.requiredCount + diff == 0);
  t.requiredCount += diff;
  if (changeImage && t.errorImage != t.formErrorOn.src)
    t.displayError(false);
};

/**
 * Get a field by its data-fid attribute.
 * @param fid - The data-fid attribute value
 * @param scope - Optional scope element (defaults to document)
 * @returns The FormElementWrapper or null
 */
function getFieldByFID(fid: string, scope?: Document | Element): FormElementWrapperInstance | null {
  const rawEle = (scope ? scope : document).querySelector(`[data-fid=${fid}]`) as HTMLElement | null;
  if (!rawEle || !(rawEle as any)?.name) { return null; }
  return getElementObj((rawEle as any).name);
}

//================================== FormElementWrapper class ==================================

/**
 * Wrapper constructor function for form elements with validation support.
 */
function FormElementWrapper(this: FormElementWrapperInstance, element: FormElementInterface, formObj: FormValidatorInstance): void {
  const t = this;
  t.formObj = formObj;
  t.name = element.name;
  t.type = element.type;
  t.events = [];

  const type = getAndSetType(t.type);
  t.initSetGet(type);
  if (t.type == "checkbox" || t.type == "radio") {
    t.element = [];
    (t.element as FormElementInterface[])[0] = element;
  } else {
    t.element = element;
  }
  if (type == "select") {
    t.numSelected = numSelectedselectFEW;
    if (isSelectToolsLoaded())
      addAdvancedProperties(this);
  } else if (type == "check")
    t.numSelected = numSelectedcheckFEW;

  t.init();
}

FormElementWrapper.prototype.addElement = addElementFEW;
FormElementWrapper.prototype.setValidation = setValidationFEW;
FormElementWrapper.prototype.validate = validateFEW;
FormElementWrapper.prototype.addEvent = addEventFEW;
FormElementWrapper.prototype.focus = focusFEW;
FormElementWrapper.prototype.trapEvents = trapEventsFEW;
FormElementWrapper.prototype.registerEvent = registerEventFEW;
FormElementWrapper.prototype.executeEvent = executeEventFEW;
FormElementWrapper.prototype.forceError = forceErrorFEW;
FormElementWrapper.prototype.showStatus = showStatusFEW;
FormElementWrapper.prototype.setRequired = setRequiredFEW;
FormElementWrapper.prototype.getSibling = getSiblingFEW;
FormElementWrapper.prototype.validateSibling = validateSiblingFEW;
FormElementWrapper.prototype.setMessage = setMessageFEW;
FormElementWrapper.prototype.compare = compareStubFEW;
FormElementWrapper.prototype.compareNullable = compareNullableFEW;
FormElementWrapper.prototype.hasChanged = hasChangedFEW;
FormElementWrapper.prototype.init = initFEW;
FormElementWrapper.prototype.isCollapsed = isCollapsedFEW;
FormElementWrapper.prototype.reset = resetFEW;

FormElementWrapper.prototype.initSetGet = function(this: FormElementWrapperInstance, type: string): void {
  const t = this;
  switch (type) {
    case 'select':
      t.get = getselectFEW;
      t.set = setselectFEW;
      break;
    case 'check':
      t.get = getcheckFEW;
      t.set = setcheckFEW;
      break;
    case 'file':
      t.get = getfileFEW;
      t.set = setfileFEW;
      break;
    case 'textarea':
      t.get = gettextareaFEW;
      t.set = settextareaFEW;
      break;
    case 'text':
      t.get = gettextFEW;
      t.set = settextFEW;
      break;
  }
};

function initFEW(this: FormElementWrapperInstance): void {
  const t = this;
  t.lastVal = t.get();
  t.trapEvents();

  const imgName = t.formObj.imgPrefix + t.name;
  t.image = (document.images as any)[imgName] as HTMLImageElement | undefined;
  t.checkValid = validateRequired;
  t.message = "";
  t.required = false;
  t.isValid = true;
  t.showStatus();
}

function addElementFEW(this: FormElementWrapperInstance, element: FormElementInterface): void {
  const t = this;
  const thisType = getAndSetType(t.type);
  const elementType = getAndSetType(element.type);
  if (thisType == "check") {
    if (t.type == element.type) {
      (t.element as FormElementInterface[])[(t.element as FormElementInterface[]).length] = element;
      t.trapEvents();
    }
  } else if (t.type == "hidden" && elementType == "check") {
    t.type = element.type;
    t.element = [];
    (t.element as FormElementInterface[])[0] = element;
    t.get = getcheckFEW;
    t.set = setcheckFEW;
    t.trapEvents();
    t.numSelected = numSelectedcheckFEW;
  } else if (t.type == "hidden" && elementType == "select") {
    t.type = element.type;
    t.element = element;
    t.get = getselectFEW;
    t.set = setselectFEW;
    t.trapEvents();
    t.numSelected = numSelectedselectFEW;
    if (isSelectToolsLoaded())
      addAdvancedProperties(t);
  }
  t.lastVal = t.get();
}

function setValidationFEW(this: FormElementWrapperInstance, validationFunction: (() => boolean) | null, required?: boolean, alwaysValidate?: boolean | null): FormElementWrapperInstance {
  const t = this;
  if (validationFunction != null) t.checkValid = validationFunction;
  if (t.required != !(required == false))
    t.formObj.changeRequired(t.required ? -1 : 1);
  t.required = !(required == false);
  if (alwaysValidate !== null) {
    t.alwaysValidate = alwaysValidate;
  }
  t.showStatus();
  return this;
}

function setRequiredFEW(this: FormElementWrapperInstance, required: boolean): FormElementWrapperInstance {
  return this.setValidation(null, required);
}

function isCollapsedFEW(this: FormElementWrapperInstance): boolean {
  const t = this;
  const type = t.type;
  if (type == "hidden") return t.image == null || !t.image.checkVisibility();
  if (type == "textarea" && t.zesty) return !(t.zesty.contentAreaContainer as HTMLElement).checkVisibility();
  if (type != "checkbox" && type != "radio") return !((t.element as FormElementInterface) as unknown as HTMLElement).checkVisibility();
  for (const i in (t.element as FormElementInterface[])) {
    if (((t.element as FormElementInterface[])[i] as unknown as HTMLElement).checkVisibility()) return false;
  }
  return true;
}

function resetFEW(this: FormElementWrapperInstance): string {
  const t = this;
  const type = getAndSetType(t.type);
  if (type == "check") {
    for (const i in (t.element as FormElementInterface[])) {
      (t.element as FormElementInterface[])[i].checked = (t.element as FormElementInterface[])[i].defaultChecked;
    }
  } else if (type == "select") {
    const selectEl = t.element as unknown as HTMLSelectElement;
    const options = selectEl.querySelectorAll("option");
    options.forEach(function(option: HTMLOptionElement) {
      option.selected = option.defaultSelected;
    });
  } else {
    t.set((t.element as FormElementInterface).defaultValue ?? "");
  }
  return t.get();
}

function validateFEW(this: FormElementWrapperInstance): boolean {
  const t = this;
  const formObj = t.formObj;
  const cm = getCM();
  if (cm) cm.suspend(this);
  const origRequired = t.required;
  if (t.required && t.isCollapsed()) {
    t.required = false;
  }
  const validBlank = (!t.required && t.get() == "");
  let isValid = t.alwaysValidate ? ((t.checkValid() as boolean) || validBlank) : (validBlank || (t.checkValid() as boolean));
  t.required = origRequired;
  if (!isValid && t.isCollapsed()) {
    if (t.get() != t.reset() && !t.resetting) {
      t.resetting = true;
      t.executeEvent("change");
      t.resetting = false;
    }
    isValid = true;
  }
  if (cm) cm.resume(this);
  if (isValid != t.isValid) {
    t.isValid = isValid;
    formObj.invalidCount += isValid ? -1 : 1;
    if (formObj.invalidCount == 0) formObj.displayError(false);
  }
  if (isValid) t.message = "";
  t.showStatus();
  if (aggressiveChangeInProgress != t.name) t.lastVal = t.get();
  if (t.proxyFor) {
    let proxyFor = t.proxyFor;
    if (typeof proxyFor == "string") {
      proxyFor = t.proxyFor = t.getSibling(proxyFor)!;
    }
    if (!(proxyFor as FormElementWrapperInstance).proxy) {
      (proxyFor as FormElementWrapperInstance).checkValid = validateProxy;
      (proxyFor as FormElementWrapperInstance).proxy = t;
      (proxyFor as FormElementWrapperInstance).alwaysValidate = true;
    }
    (proxyFor as FormElementWrapperInstance).validate();
  }
  return isValid;
}

function addEventFEW(this: FormElementWrapperInstance, event: string, functionName: string): void {
  event = event.toLowerCase();
  this.registerEvent(functionName + "(thisObj)", event, null);
}

function focusFEW(this: FormElementWrapperInstance): void {
  const t = this;
  if (t.type == "hidden") return;
  setCurField(this);
  if (getAndSetType(t.type) == "check") {
    for (let i = 0; i < (t.element as FormElementInterface[]).length; i++) {
      if ((t.element as FormElementInterface[])[i].checked) {
        (t.element as FormElementInterface[])[i].focus();
        return;
      }
    }
    (t.element as FormElementInterface[])[0].focus();
  } else {
    (t.element as FormElementInterface).focus();
  }
}

function trapEventsFEW(this: FormElementWrapperInstance): void {
  let which: number | null = null;
  const t = this;
  const thisType = getAndSetType(t.type);
  let element: FormElementInterface = t.element as FormElementInterface;
  if (thisType == "check") {
    which = (t.trappedElements == null) ? 0 : t.trappedElements + 1;
    t.trappedElements = which;
    element = (t.element as FormElementInterface[])[which];
    if ((t.element as FormElementInterface[]).length > which + 1)
      t.trapEvents();
  }
  if (t.type != "button" && t.type != "submit" && t.type != "reset" && t.type != "image") {
    const events = t.type == "hidden" ? "change" : ("focus blur change" + (thisType == "check" ? " click" : ""));
    const eventList = events.split(" ");
    const htmlElement = element as unknown as HTMLElement;
    for (const i in eventList) {
      const ev = eventList[i];
      htmlElement.addEventListener(ev, genericFormEventHandler);
      const fn = getEventFn(element, ev);
      if (fn) {
        t.registerEvent(fn, ev, which);
        clearGenericEvent(element, ev);
      }
    }
  }
}

function clearGenericEvent(el: FormElementInterface, ev: string): void {
  switch (ev) {
    case 'focus': el.onfocus = function() {}; break;
    case 'blur': el.onblur = function() {}; break;
    case 'click': el.onclick = function() {}; break;
    case 'change': el.onchange = function() {}; break;
  }
}

function getEventFn(el: FormElementInterface, ev: string): (() => void) | null {
  switch (ev) {
    case 'focus': return el.onfocus ?? null;
    case 'blur': return el.onblur ?? null;
    case 'click': return el.onclick ?? null;
    case 'change': return el.onchange ?? null;
  }
  return null;
}

function doGenericEvent(tp: string, t: FormElementWrapperInstance): void {
  switch (tp) {
    case "blur": generic_blur(t); break;
    case "change": generic_change(t); break;
    case "click":
    case "focus": generic_focus(t); break;
  }
}

function registerEventFEW(this: FormElementWrapperInstance, code: string | (() => void), eventName: string, which: number | null): void {
  eventName = eventName.toLowerCase();
  if (eventName.substring(0, 2) == "on") eventName = eventName.substring(2);
  if (eventName != "click" && eventName != "change" && eventName != "blur" && eventName != "focus" && eventName != "submit") {
    return;
  }
  this.events[this.events.length] = new (EventObject as any)(code, eventName, which);
}

function showStatusFEW(this: FormElementWrapperInstance): void {
  const t = this;
  if (t.image) {
    t.image.src = t.isValid ? (t.required ? (t.get() == "" ? t.formObj.fieldErrorRequiredRed.src : t.formObj.fieldErrorRequiredBlack.src) : t.formObj.fieldErrorOff.src) : t.formObj.fieldErrorOn.src;
    t.image.alt = t.message;
    t.image.title = t.message;
  }
  if (curField == this) {
    let inputEl: HTMLElement | null = null;
    if (t.zesty) {
      inputEl = (t.zesty.getContainer() as HTMLElement).querySelector(".mceLayout");
    }
    fieldMessage(t.message, inputEl);
  }
  window.status = t.message;
}

function forceErrorFEW(this: FormElementWrapperInstance, mssg?: string | null): void {
  const t = this;
  if (t.isValid) {
    t.formObj.invalidCount++;
    t.isValid = false;
  }
  if (mssg != null) t.setMessage(mssg);
  t.showStatus();
  t.focus();
}

function getSiblingFEW(this: FormElementWrapperInstance, name: string): FormElementWrapperInstance | null {
  return this.formObj.getElement(name);
}

function validateSiblingFEW(this: FormElementWrapperInstance, sibling: string | FormElementWrapperInstance): void {
  if (typeof sibling == "string") sibling = this.formObj.getElement(sibling)!;
  validateValue(sibling, this.name);
}

function setMessageFEW(this: FormElementWrapperInstance, mssg: string): boolean {
  this.message = mssg;
  return mssg == "" || mssg == null;
}

function executeEventFEW(this: FormElementWrapperInstance, type: string, which?: number): void {
  const t = this;
  if (!(t.element as FormElementInterface[]).length) { // not multi-element
    (t.element as unknown as HTMLElement).dispatchEvent(new Event(type, { bubbles: true }));
    return;
  }
  doGenericEvent(type, t);

  const events = t.events;
  for (const i in events)
    if (events[i].type == type && (events[i].which == null || events[i].which == which)) {
      executeCode(t, t.formObj.element, t.element, events[i].getCode());
    }
}

let thisObj: FormElementWrapperInstance = null as any;
let form: HTMLFormElement = null as any;

function executeCode(t: FormElementWrapperInstance, f: HTMLFormElement, element: FormElementInterface | FormElementInterface[], code: () => void): void {
  const oldObj = thisObj;
  const oldForm = form;
  thisObj = t;
  form = f;
  // Also update globalThis so dynamically evaluated code (new Function) can access them
  (globalThis as any).thisObj = t;
  (globalThis as any).form = f;
  code.call(Array.isArray(element) ? element[0] : element);
  thisObj = oldObj;
  form = oldForm;
  // Restore global values
  (globalThis as any).thisObj = oldObj;
  (globalThis as any).form = oldForm;
}

//---------------------------------- get() and set() functions ----------------------------------

/**
 * Determine the category type for a form element type.
 * @param type - The element type
 * @returns The category type
 */
function getAndSetType(type: string): string {
  type = type.toLowerCase();
  if (type.substring(0, 6) == "select") return "select";
  if (type == "radio" || type == "checkbox") return "check";
  if (type == "file") return "file";
  if (type == "hidden" || type == "textarea") return "textarea";
  return "text";
}

function getcheckFEW(this: FormElementWrapperInstance, which?: string): string {
  const t = this;
  if (which != null) {
    for (let i = 0; i < (t.element as FormElementInterface[]).length; i++)
      if ((t.element as FormElementInterface[])[i].value == which)
        return (t.element as FormElementInterface[])[i].checked ? which : "";
    return "";
  }
  let returnVal = "";
  for (let i = 0; i < (t.element as FormElementInterface[]).length; i++)
    if ((t.element as FormElementInterface[])[i].checked)
      returnVal += VALUE_SPACER + (t.element as FormElementInterface[])[i].value;
  return returnVal.substring(1);
}

function setcheckFEW(this: FormElementWrapperInstance, val: string, which?: string): void {
  const t = this;
  const cm = getCM();
  if (cm) cm.fieldChanged(this);
  if (which != null) {
    for (let i = 0; i < (t.element as FormElementInterface[]).length; i++)
      if ((t.element as FormElementInterface[])[i].value == which) {
        (t.element as FormElementInterface[])[i].checked = (val > "");
        break;
      }
    return;
  }
  val = VALUE_SPACER + val + VALUE_SPACER;
  for (let i = 0; i < (t.element as FormElementInterface[]).length; i++)
    (t.element as FormElementInterface[])[i].checked = (val.indexOf(VALUE_SPACER + (t.element as FormElementInterface[])[i].value + VALUE_SPACER) != -1);
}

function numSelectedcheckFEW(this: FormElementWrapperInstance): number {
  let returnVal = 0;
  for (let i = 0; i < (this.element as FormElementInterface[]).length; i++)
    if ((this.element as FormElementInterface[])[i].checked)
      returnVal++;
  return returnVal;
}

function getselectFEW(this: FormElementWrapperInstance, which?: string): string {
  const t = this;
  const element = t.element as FormElementInterface;
  if (which != null) {
    for (let i = 0; i < element.options!.length; i++)
      if (element.options![i].value == which)
        return element.options![i].selected ? which : "";
    return "";
  }
  let returnVal = "";
  for (let i = 0; i < element.options!.length; i++)
    if (element.options![i].selected)
      returnVal += VALUE_SPACER + element.options![i].value;
  return returnVal.substring(1);
}

function setselectFEW(this: FormElementWrapperInstance, val: string, which?: string): void {
  const t = this;
  const element = t.element as FormElementInterface;
  const cm = getCM();
  if (cm) cm.fieldChanged(this);
  if (which != null) {
    for (let i = 0; i < element.options!.length; i++)
      if (element.options![i].value == which) {
        element.options![i].selected = (val > "");
        break;
      }
    return;
  }
  val = VALUE_SPACER + val + VALUE_SPACER;
  for (let i = 0; i < element.options!.length; i++)
    element.options![i].selected = (val.indexOf(VALUE_SPACER + element.options![i].value + VALUE_SPACER) != -1);
}

function numSelectedselectFEW(this: FormElementWrapperInstance): number {
  const element = this.element as FormElementInterface;
  let returnVal = 0;
  for (let i = 0; i < element.options!.length; i++)
    if (element.options![i].selected)
      returnVal++;
  return returnVal;
}

function gettextFEW(this: FormElementWrapperInstance): string {
  return (this.element as FormElementInterface).value;
}

function settextFEW(this: FormElementWrapperInstance, val: string): void {
  if (aggressiveChangeInProgress == this.name) return;
  const cm = getCM();
  if (cm) cm.fieldChanged(this);
  const length = (this.element as FormElementInterface).maxLength;
  if (length && length > 0 && length < val.length) {
    val = val.substring(0, length);
  }
  (this.element as FormElementInterface).value = val;
}

function gettextareaFEW(this: FormElementWrapperInstance): string {
  return (this.element as FormElementInterface).value;
}

function settextareaFEW(this: FormElementWrapperInstance, val: string): void {
  if (aggressiveChangeInProgress == this.name) return;
  const cm = getCM();
  if (cm) cm.fieldChanged(this);
  const length = this.maxLength;
  if (length && length < val.length) {
    val = val.substring(0, length);
  }
  (this.element as FormElementInterface).value = val;
}

function getfileFEW(this: FormElementWrapperInstance): string {
  const val = (this.element as FormElementInterface).value;
  if (val > "") return val;
  const shadow = this.getSibling("__" + this.name);
  return shadow == null ? "" : shadow.get();
}

function setfileFEW(this: FormElementWrapperInstance, val: string): void {
  const shadow = this.getSibling("__" + this.name);
  if (shadow != null) shadow.set(val);
}

//================================== EventObject Class ==================================

/**
 * Event handler constructor function for form elements.
 */
function EventObject(this: EventObjectInstance, code: string | (() => void), type: string, which: number | null): void {
  this.code = code;
  this.type = type.toLowerCase();
  this.which = which;
}

EventObject.prototype.getCode = function(this: EventObjectInstance): () => void {
  if (typeof this.code != "function") {
    this.code = new Function(this.code as string) as () => void;
  }
  return this.code as () => void;
};

//================================== Compare Functions ==================================

function compareStubFEW(this: FormElementWrapperInstance, value: any): number | null {
  const fn = this.checkValid(true);
  this.compare = (typeof fn == "function") ? fn as ((value: any) => number | null) : compareFEW;
  return this.compare(value);
}

function compareFEW(this: FormElementWrapperInstance, value: any): number | null {
  validateValue(value, this.name);

  value = unwrapElement(value);
  if (typeof value == "number" || typeof value == "boolean")
    value = "" + value;
  else if (value != null && typeof value != "string")
    return null;

  const thisValue = this.get();
  if (value == null) return thisValue == "" || thisValue == null ? 0 : null;
  if (thisValue == value) return 0;
  if (thisValue > value) return 1;
  return -1;
}

function compareConvertedFEW(this: FormElementWrapperInstance, value: any): number | null {
  validateValue(value, this.name);
  value = getValueFor(unwrapElement(value), this.converter!);
  const thisValue = getValueFor(this.get(), this.converter!);
  if (value == null || thisValue == null) return value == thisValue ? 0 : null;
  return thisValue - value;
}

function compareNullableFEW(this: FormElementWrapperInstance, val: any, op: string, nullRtn?: boolean): boolean {
  const c = this.compare(val);
  if (c == null) return nullRtn == null ? op == "!=" : nullRtn;
  switch (op) {
    case "==": return c == 0;
    case "!=": return c != 0;
    case ">=": return c >= 0;
    case "<=": return c <= 0;
    case ">": return c > 0;
    case "<": return c < 0;
  }
  return false;
}

const curValidate: { [key: string]: boolean | null } = {};

function validateValue(value: any, curName: string): void {
  if (value != null && typeof value == "object"
    && typeof value.validate == "function"
    && typeof value.get == "function"
    && value.get() > ""
    && !curValidate[value.name]
  ) {
    curValidate[curName] = true;
    value.validate();
    curValidate[curName] = null;
  }
}

function unwrapElement(value: any): any {
  if (value != null && typeof value == "object" && typeof value.get == "function") return value.get();
  return value;
}

function getValueFor(value: any, convertFunction: (value: string) => any): any {
  value = unwrapElement(value);
  if (typeof value == "string") return (value > "") ? convertFunction(value) : null;
  return value;
}

function hasChangedFEW(this: FormElementWrapperInstance, ignoreSelect?: boolean): boolean {
  return this.get() != this.lastVal && (ignoreSelect || this.type.indexOf('select') == -1);
}

//---------------------------------- Event Trapping ----------------------------------
/** @deprecated Always false - IE is no longer supported */
const isIE = false;

//---------------------------------- Event Handling ----------------------------------
function genericFormEventHandler(this: FormElementInterface, e: Event): void {
  const elementWrapper = getFormElementWrapper(this);
  const type = e.type;
  doGenericEvent(type, elementWrapper);

  const events = elementWrapper.events;
  for (const i in events) {
    if (type == events[i].type && (events[i].which == null || (elementWrapper.element as FormElementInterface[])[events[i].which!] == this)) {
      executeCode(elementWrapper, elementWrapper.formObj.element, this, events[i].getCode());
    }
  }
}

function generic_focus(e: FormElementWrapperInstance): void {
  if (curField != null && curField.hasChanged(true)) curField.executeEvent('change');
  setCurField(e);
  e.showStatus();
}

function generic_blur(e: FormElementWrapperInstance): void {
  const cm = getCM();
  if (cm) cm.fieldBlur(e);
  setCurField(null);
}

function generic_change(e: FormElementWrapperInstance): void {
  setCurField(e);
  const cm = getCM();
  if (cm) cm.fieldChanged(e);
  e.validate();
}

let aggressiveChangeInProgress = "";

function aggressiveChange(elementWrapper: FormElementWrapperInstance): void {
  aggressiveChangeInProgress = elementWrapper.name;
  elementWrapper.executeEvent("change");
  aggressiveChangeInProgress = "";
}

function submitEvent(this: HTMLFormElement, e: Event): void {
  setCurFormObj(getFormObj(this.name));
  if (!doSubmit(null, null)) e.preventDefault();
}

function doSubmit(event: string | null, url: string | null): boolean {
  if (!submitInitiated) {
    submitInitiated = true;
    setTimeout(function() { submitInitiated = false; }, 3000);
    const cm = getCM();
    if (cm) cm.suspend();
    // Check globalThis for processSubmitParams since it may be defined by common_js.jsp after this module loads
    const globalProcessSubmitParams = (globalThis as any).processSubmitParams as typeof processSubmitParams;
    if (curFormObj.validate() && curFormObj.preSubmit(event, url)
      && (typeof globalProcessSubmitParams != "function" || globalProcessSubmitParams(curFormObj, event, url) !== false)) {
      const childElements = curFormObj.childElements;
      for (const i in childElements)
        runSubmitEvents(childElements[i]);
      if (cm) { cm.record(curFormObj); cm.allowLeave(); }
      return true;
    }
    if (cm) cm.resume();
    submitInitiated = false;
  }
  return false;
}

function runSubmitEvents(elementWrapper: FormElementWrapperInstance): void {
  const events = elementWrapper.events;
  for (const i in events)
    if (events[i].type == "submit") {
      executeCode(elementWrapper, elementWrapper.formObj.element, elementWrapper.element, events[i].getCode());
    }
}

// Document ready handler
function initKeyupHandler(): void {
  document.addEventListener("keyup", function(e: KeyboardEvent) {
    const key = e.which;
    if (curField) {
      if (key == 13 && curField.type != 'textarea') {
        const temp = curField;
        if (curField.type != 'radio' && curField.type != 'checkbox') curField.executeEvent('blur');
        submitForm(temp.formObj.name);
      } else if ((isIE ? false : key != 9) || key == 8 || key == 32 || key == 46 || key >= 48 && key <= 57 || key >= 65 && key <= 90 || key >= 96 && key <= 111 || key >= 186 && key <= 192 || key >= 219 && key <= 222) {
        const cm = getCM();
        if (cm) cm.keyPress();
        if (curField.aggressiveOnChange || curField.isValid == false) {
          window.setTimeout(function() { aggressiveChange(curField!); }, 10);
        }
      }
    }
    if ((window as any).routeEvent) (window as any).routeEvent(e);
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initKeyupHandler);
  } else {
    initKeyupHandler();
  }
}

/**
 * Display a field message tooltip.
 * @param message - The message to display
 * @param inputEl - HTML element for positioning
 */
function fieldMessage(message?: string | null, inputEl?: HTMLElement | null): void {
  const fieldMessageEl = document.getElementById("fieldMessage");
  if (!fieldMessageEl) return;
  if (message && message.length > 0) {
    if (inputEl) {
      const rect = inputEl.getBoundingClientRect();
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      fieldMessageEl.style.left = (rect.left + scrollLeft) + 'px';
      fieldMessageEl.style.top = (rect.top + scrollTop + inputEl.offsetHeight) + 'px';
    }
    message = message.replace("/\\n/g", "<br>");
    fieldMessageEl.innerHTML = message;
    fieldMessageEl.style.display = '';
  } else {
    fieldMessageEl.style.display = 'none';
  }
}

// Export for module usage
export {
  _ERROR_REPORTER_,
  _VALIDATE_GROUP_,
  _CM_,
  fieldProblem,
  fieldOff,
  fieldRequiredRed,
  fieldRequiredBlack,
  formProblem,
  formOff,
  formRequired,
  formElements,
  formObjs,
  curFormObj,
  curField,
  firstField,
  focusField,
  errorImgPath,
  validateIsOff,
  submitInitiated,
  VALUE_SPACER,
  processSubmitParams,
  getFormObj,
  getElementObj,
  getEl,
  get$,
  getValue,
  setValue,
  getFormValidator,
  getFormElementWrapper,
  enableValidation,
  disableValidation,
  setImagePath,
  submitForm,
  submitFormVia,
  getPendingSubmitSender,
  saveViaAction,
  LOWERALPHA,
  UPPERALPHA,
  ALPHA,
  NUMERIC,
  ALPHANUMERIC,
  extract,
  strip,
  replace,
  BASE_STATIC_RESOURCE,
  preloadImg,
  makeSet,
  validateRequired,
  validateProxy,
  FormValidator,
  getFieldByFID,
  FormElementWrapper,
  EventObject,
  getAndSetType,
  compareFEW,
  compareConvertedFEW,
  curValidate,
  validateValue,
  unwrapElement,
  getValueFor,
  isIE,
  aggressiveChangeInProgress,
  aggressiveChange,
  doSubmit,
  fieldMessage,
  thisObj,
  form
};

// Export types
export type {
  ChangeManagement,
  FormElementInterface,
  EventObjectInstance,
  FormValidatorInstance,
  FormElementWrapperInstance,
  ZestyEditor,
  PageNavigator,
  ActionSaveResult
};

// Expose on globalThis for backwards compatibility
const _global = globalThis as any;
_global._ERROR_REPORTER_ = _ERROR_REPORTER_;
// Note: _SELECT_TOOLS_ is defined by selecttools.ts, _VALIDATE_GROUP_, _CM_, and processSubmitParams may be defined by other modules
// Only export if not already set to avoid overwriting valid values
if (_global._VALIDATE_GROUP_ === undefined) _global._VALIDATE_GROUP_ = _VALIDATE_GROUP_;
if (_global._CM_ === undefined) _global._CM_ = _CM_;
_global.fieldProblem = fieldProblem;
_global.fieldOff = fieldOff;
_global.fieldRequiredRed = fieldRequiredRed;
_global.fieldRequiredBlack = fieldRequiredBlack;
_global.formProblem = formProblem;
_global.formOff = formOff;
_global.formRequired = formRequired;
_global.formElements = formElements;
_global.formObjs = formObjs;
_global.curFormObj = curFormObj;
_global.curField = curField;
_global.firstField = firstField;
_global.focusField = focusField;
_global.errorImgPath = errorImgPath;
_global.validateIsOff = validateIsOff;
_global.submitInitiated = submitInitiated;
_global.VALUE_SPACER = VALUE_SPACER;
if (_global.processSubmitParams === undefined) _global.processSubmitParams = processSubmitParams;
_global.getFormObj = getFormObj;
_global.getElementObj = getElementObj;
_global.getEl = getEl;
_global.get$ = get$;
_global.getValue = getValue;
_global.setValue = setValue;
_global.getFormValidator = getFormValidator;
_global.getFormElementWrapper = getFormElementWrapper;
_global.enableValidation = enableValidation;
_global.disableValidation = disableValidation;
_global.setImagePath = setImagePath;
_global.submitForm = submitForm;
_global.submitFormVia = submitFormVia;
_global.getPendingSubmitSender = getPendingSubmitSender;
_global.LOWERALPHA = LOWERALPHA;
_global.UPPERALPHA = UPPERALPHA;
_global.ALPHA = ALPHA;
_global.NUMERIC = NUMERIC;
_global.ALPHANUMERIC = ALPHANUMERIC;
_global.extract = extract;
_global.strip = strip;
_global.replace = replace;
_global.BASE_STATIC_RESOURCE = BASE_STATIC_RESOURCE;
_global.preloadImg = preloadImg;
_global.makeSet = makeSet;
_global.validateRequired = validateRequired;
_global.validateProxy = validateProxy;
_global.FormValidator = FormValidator;
_global.getFieldByFID = getFieldByFID;
_global.FormElementWrapper = FormElementWrapper;
_global.EventObject = EventObject;
_global.getAndSetType = getAndSetType;
_global.compareFEW = compareFEW;
_global.compareConvertedFEW = compareConvertedFEW;
_global.curValidate = curValidate;
_global.validateValue = validateValue;
_global.unwrapElement = unwrapElement;
_global.getValueFor = getValueFor;
_global.isIE = isIE;
_global.aggressiveChangeInProgress = aggressiveChangeInProgress;
_global.aggressiveChange = aggressiveChange;
_global.doSubmit = doSubmit;
_global.fieldMessage = fieldMessage;
_global.thisObj = thisObj;
_global.form = form;

// FEW functions for FieldGroup and other extensions
_global.setValidationFEW = setValidationFEW;
_global.setRequiredFEW = setRequiredFEW;
_global.validateFEW = validateFEW;
_global.validateSiblingFEW = validateSiblingFEW;
_global.showStatusFEW = showStatusFEW;
_global.addEventFEW = addEventFEW;
_global.registerEventFEW = registerEventFEW;
_global.forceErrorFEW = forceErrorFEW;
_global.getSiblingFEW = getSiblingFEW;
_global.setMessageFEW = setMessageFEW;
_global.compareNullableFEW = compareNullableFEW;
_global.hasChangedFEW = hasChangedFEW;
_global.initFEW = initFEW;

// Script resource notification
if (typeof _SR_ !== "undefined" && _SR_ !== null) {
  _SR_.notify("errorreporter.js");
}
